import React, { useRef, useState } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { ImagePlus, RotateCcw } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CHECKER = {
  backgroundColor: "#141414",
  backgroundImage:
    "linear-gradient(45deg, #1f1f1f 25%, transparent 25%), linear-gradient(-45deg, #1f1f1f 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #1f1f1f 75%), linear-gradient(-45deg, transparent 75%, #1f1f1f 75%)",
  backgroundSize: "16px 16px",
  backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0",
};

export const BrandLogoField = ({ kind, label, defaultSrc, logo, onChanged }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [warn, setWarn] = useState("");

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setError("");
    setWarn("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/settings/brand-logos/${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.transparent === false) setWarn("Atenção: este PNG não tem fundo transparente.");
      await onChanged();
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const reset = async () => {
    if (!window.confirm(`Voltar à logo padrão ${label}? Os PDFs passam a usar a versão embutida no sistema.`)) return;
    setBusy(true);
    setError("");
    setWarn("");
    try {
      await api.delete(`/settings/brand-logos/${kind}`);
      await onChanged();
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const custom = Boolean(logo?.path);
  const src = custom ? `${BACKEND}/api/public/files/${logo.path}` : defaultSrc;

  return (
    <div className="card p-5 space-y-4" data-testid={`brand-logo-${kind}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="overline">Logo {label}</div>
          <div className="text-xs text-[var(--text-secondary)] mt-1" data-testid={`brand-logo-status-${kind}`}>
            {custom ? "Personalizada (enviada pelo admin)" : "Padrão do sistema"}
          </div>
        </div>
        <span
          className="text-[10px] uppercase tracking-widest px-2 py-1 border"
          style={{
            borderColor: custom ? "#C9A84C" : "rgba(255,255,255,0.15)",
            color: custom ? "#C9A84C" : "var(--text-secondary)",
          }}
        >
          {custom ? "Custom" : "Padrão"}
        </span>
      </div>

      <div className="h-32 border border-white/10 flex items-center justify-center p-4" style={CHECKER}>
        <img
          data-testid={`brand-logo-preview-${kind}`}
          src={src}
          alt={`Logo ${label}`}
          className="max-h-full max-w-full object-contain"
        />
      </div>

      <input
        ref={inputRef}
        data-testid={`brand-logo-input-${kind}`}
        type="file"
        accept="image/png"
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
      />
      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          data-testid={`brand-logo-upload-${kind}`}
          className="btn-primary !py-2 !text-xs inline-flex items-center gap-1"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
        >
          <ImagePlus size={13} /> {busy ? "Enviando..." : custom ? "Trocar PNG" : "Enviar PNG"}
        </button>
        {custom && (
          <button
            type="button"
            data-testid={`brand-logo-reset-${kind}`}
            className="btn-ghost !py-2 !text-xs inline-flex items-center gap-1"
            disabled={busy}
            onClick={reset}
          >
            <RotateCcw size={13} /> Voltar ao padrão
          </button>
        )}
      </div>
      <div className="text-[11px] text-[var(--text-secondary)]">
        PNG com fundo transparente, até 3 MB. Usada no rodapé de todos os PDFs gerados.
      </div>
      {warn && <div className="text-[11px] text-[#F5C451]" data-testid={`brand-logo-warn-${kind}`}>{warn}</div>}
      {error && <div className="text-[11px] text-[#FF6B6B]" data-testid={`brand-logo-error-${kind}`}>{error}</div>}
    </div>
  );
};
